//@ts-nocheck
import { css } from "@compiled/react";

import Lozenge from "@atlaskit/lozenge/dist/types/Lozenge";
import { media } from "@atlaskit/primitives";

import {
    ContentBlock,
    OneButtonProps
} from "@/components/wpl/content-block/src";
import { AspectRatios, Picture } from "@/components/wpl/image/src";
import { Link } from "@/libraries/design/components";
import { Row, Layout } from "@/libraries/wpl/foundations/src";

export type ImagePositions = "left" | "right";
type AlignTypes = "default" | "top" | "bottom" | "center";

export interface SideBySideProps {
    eyebrowText?: string;
    headerText: string | React.ReactNode;
    isBannerRichText?: boolean;
    headerType?: "h1" | "h2" | "h3" | "h4" | "h5" | undefined;
    lozenge?: string;
    bodyText?: string | React.ReactNode;
    linkText?: string;
    linkUrl?: string;
    imageLink?: string;
    imageAltText?: string;
    imagePosition?: ImagePositions;
    className?: string;
    ctaProps?: OneButtonProps;
    aspectRatio?: AspectRatios;
    horizontalRule?: boolean;
    alignItems?: AlignTypes;
    rowProps?: Layout;
    backgroundColor?: string;
    margin?: string;
}

const simpleImageTextSideStyle = css({
    padding: "80px 0",

    [media.below.sm]: {
        padding: "16px 0"
    },
    [media.only.sm]: {
        padding: "60px 0"
    }
});

interface RenderImageProps {
    imageLink: string;
    imageAltText: string;
    aspectRatio: AspectRatios;
}

const RenderImage = ({
    imageLink,
    imageAltText,
    aspectRatio
}: RenderImageProps) => (
    <Picture
        css={css({
            // Picture component does not accept compiled CSS. Using nested selector to apply CSS to displayed image.
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            img: {
                maxWidth: "100%",
                objectFit: "contain",
                width: "100%",
                alignSelf: "baseline",
                height: "auto"
            },

            [media.below.sm]: {
                order: 1
            }
        })}
        image={{
            src: imageLink,
            alt: imageAltText,
            aspectRatio
        }}
    />
);

interface RenderContentProps {
    eyebrowText?: string;
    headerText: string | React.ReactNode;
    isBannerRichText?: boolean;
    headerType?: "h1" | "h2" | "h3" | "h4" | "h5" | undefined;
    lozenge?: string;
    bodyText?: string | React.ReactNode;
    ctaProps?: OneButtonProps;
    horizontalRule?: boolean;
    linkText?: string;
    linkUrl?: string;
}

const RenderContent = ({
    eyebrowText,
    headerText,
    isBannerRichText,
    headerType,
    lozenge,
    bodyText,
    ctaProps,
    horizontalRule,
    linkText,
    linkUrl
}: RenderContentProps) => (
    <div>
        <ContentBlock
            eyebrowText={eyebrowText}
            headerText={headerText}
            isBannerRichText={isBannerRichText}
            headerType={headerType}
            lozenge={lozenge}
            bodyText={bodyText}
            ctaProps={ctaProps}
            horizontalRule={horizontalRule}
        />
        {linkText && linkUrl && (
            <Link text={linkText} href={linkUrl} type="primary" />
        )}
        <div
            css={css({
                "display": "flex",
                // Avoid breaking change since class-name "image-text-side--text-input" is NOT in repo
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                ".image-text-side--text-input": {
                    height: "40px"
                },
                // Avoid breaking change since class-name "image-text-side--text-input" is NOT in repo
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                [`.image-text-side--text-input label[for="text-input"]`]: {
                    display: "none"
                },
                // Avoid breaking change since class-name "image-text-side--text-input" is NOT in repo
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                ".image-text-side--text-input div.input-container": {
                    height: "40px",
                    marginInlineEnd: "16px"
                },
                // Avoid breaking change since class-name "image-text-side--text-input" is NOT in repo
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                ".image-text-side--text-input input": {
                    height: "40px"
                }
            })}
        />
    </div>
);

export const SimpleImageTextSide = ({
    eyebrowText,
    headerText,
    isBannerRichText = false,
    headerType = "h2",
    lozenge,
    bodyText,
    linkText,
    linkUrl,
    imageLink,
    imageAltText,
    imagePosition,
    className = "",
    ctaProps,
    aspectRatio,
    horizontalRule = false,
    alignItems = "center",
    rowProps = "6-6",
    backgroundColor = "transparent",
    margin = "80px 0"
}: SideBySideProps) => {
    const renderImageProps = {
        imageLink,
        imageAltText,
        aspectRatio
    };

    const renderContentProps = {
        eyebrowText,
        headerText,
        isBannerRichText,
        headerType,
        lozenge,
        bodyText,
        ctaProps,
        horizontalRule,
        linkText,
        linkUrl
    };

    const first =
        imagePosition === "left" ? (
            <RenderImage {...renderImageProps} />
        ) : (
            <RenderContent {...renderContentProps} />
        );
    const second =
        imagePosition === "left" ? (
            <RenderContent {...renderContentProps} />
        ) : (
            <RenderImage {...renderImageProps} />
        );

    return (
        <div
            className={className}
            css={simpleImageTextSideStyle}
            style={{
                "--bkgd-color": backgroundColor,
                "--margin": margin,
                "backgroundColor": "var(--bkgd-color, transparent)",
                "margin": "var(--margin, 0)"
            }}
        >
            <Row layout={rowProps} alignItems={alignItems}>
                {first}
                {second}
            </Row>
        </div>
    );
};
